/**
 * DocAnalysisScreen
 *
 * CONTENT-ONLY screen reproducing the real bunker.ai "Dok.-Analyse" / document
 * intelligence feature for the guided product tour. It is a PREVIEW feature, so
 * the header carries an amber "Vorschau · kommt bald" badge plus a "bunker.ai"
 * AI badge.
 *
 * Two-region, document-intelligence layout:
 *   LEFT  (~46%)  the SOURCE DOCUMENT preview — a paper-like page with title,
 *                 body lines and highlighted passages the analysis references,
 *                 plus a page indicator.
 *   RIGHT (flex)  the ANALYSIS RESULTS panel, branching on `scenarioId`:
 *     • contractCheck — "Risiken & Fristen": a list of identified risks each
 *       with a severity chip (hoch/mittel/niedrig), a description and a
 *       "Quelle: §X / Seite Y" reference, plus a small Fristen-timeline.
 *     • docAnalysis  — "Extrahierte Kennzahlen": a KPI grid extracted from a
 *       Quartalsbericht Q3, an auto-summary and a tiny extracted table snippet.
 *
 * All visible German text is read from the `demo` namespace under
 * docScreen.<scenarioId>.* The screen is shell-agnostic and fills the available
 * area; long content scrolls internally. A subtle entrance animation is gated
 * on `active` but the layout is fully composed even when `active` is false.
 */

import {
  AlertTriangle,
  ArrowDownRight,
  ArrowUpRight,
  BarChart3,
  CalendarClock,
  FileSearch,
  FileText,
  Gavel,
  Minus,
  Percent,
  Quote,
  ScanText,
  ShieldAlert,
  Sparkles,
  Table2,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

const PREFIX = 'docScreen';

interface DocAnalysisScreenProps {
  active?: boolean;
  scenarioId: string;
  className?: string;
}

type Severity = 'high' | 'mid' | 'low';
type Trend = 'up' | 'down' | 'flat';

interface Finding {
  severity: Severity;
  category: string;
  title: string;
  detail: string;
  source: string;
}

interface Deadline {
  date: string;
  label: string;
  note: string;
}

interface Kpi {
  label: string;
  value: string;
  delta: string;
  trend: Trend;
}

const C = {
  page: 'var(--bnkp-appbg, #0D1117)',
  surface: 'var(--bnkp-surface, #141B27)',
  elevated: 'var(--bnkp-elevated, #1A2334)',
  recessed: 'var(--bnkp-recessed, #0A0F1C)',
  border: 'var(--bnkp-border, #1E2D45)',
  borderStrong: 'var(--bnkp-border2, #30415F)',
  textPrimary: 'var(--bnkp-text, #E2E8F0)',
  textSecondary: 'var(--bnkp-text2, #94A3C2)',
  textTertiary: 'var(--bnkp-text3, #4A5568)',
  blue: 'var(--bnkp-primary, #2D7EFF)',
  cyan: 'var(--bnkp-cyan, #00D4FF)',
  green: 'var(--bnkp-green, #34D399)',
  amber: 'var(--bnkp-amber, #FBBF24)',
  red: 'var(--bnkp-red, #FB7185)',
  purple: 'var(--bnkp-purple, #7C3AED)',
} as const;

const SEVERITY_STYLE: Record<Severity, { color: string; bg: string; border: string; labelKey: string }> = {
  high: { color: C.red, bg: 'rgba(251,113,133,0.12)', border: 'rgba(251,113,133,0.30)', labelKey: 'sevHigh' },
  mid: { color: C.amber, bg: 'rgba(251,191,36,0.12)', border: 'rgba(251,191,36,0.30)', labelKey: 'sevMid' },
  low: { color: C.green, bg: 'rgba(52,211,153,0.12)', border: 'rgba(52,211,153,0.28)', labelKey: 'sevLow' },
};

// Which body lines glow under each scenario's highlight sweep (index into the
// 6 document lines). contractCheck highlights the liability + price clauses;
// docAnalysis highlights the two lines that carry the headline figures.
const HIGHLIGHT: Record<string, { cyan: number; yellow: number }> = {
  contractCheck: { yellow: 1, cyan: 3 },
  docAnalysis: { cyan: 1, yellow: 2 },
};

function CategoryIcon({ severity }: { severity: Severity }) {
  const Icon = severity === 'high' ? ShieldAlert : severity === 'mid' ? AlertTriangle : Gavel;
  return <Icon size={15} strokeWidth={2} aria-hidden="true" />;
}

function TrendIcon({ trend }: { trend: Trend }) {
  if (trend === 'up') return <ArrowUpRight size={13} strokeWidth={2.4} aria-hidden="true" />;
  if (trend === 'down') return <ArrowDownRight size={13} strokeWidth={2.4} aria-hidden="true" />;
  return <Minus size={13} strokeWidth={2.4} aria-hidden="true" />;
}

export function DocAnalysisScreen({ active = true, scenarioId, className }: DocAnalysisScreenProps) {
  const { t } = useTranslation('demo');
  const isContract = scenarioId === 'contractCheck';

  // Namespaced reader: every visible string flows through docScreen.<id>.<field>.
  const k = (field: string) => `${PREFIX}.${scenarioId}.${field}`;
  const tx = (field: string) => t(k(field));

  const docLines = [
    tx('docLine1'),
    tx('docLine2'),
    tx('docLine3'),
    tx('docLine4'),
    tx('docLine5'),
    tx('docLine6'),
  ];
  const hl = HIGHLIGHT[scenarioId] ?? { cyan: -1, yellow: -1 };

  const findings = isContract
    ? (t(k('findings'), { returnObjects: true }) as unknown as Finding[])
    : [];
  const deadlines = isContract
    ? (t(k('deadlines'), { returnObjects: true }) as unknown as Deadline[])
    : [];
  const kpis = !isContract ? (t(k('kpis'), { returnObjects: true }) as unknown as Kpi[]) : [];
  const tableCols = !isContract
    ? (t(k('tableCols'), { returnObjects: true }) as unknown as string[])
    : [];
  const tableRows = !isContract
    ? (t(k('tableRows'), { returnObjects: true }) as unknown as string[][])
    : [];

  return (
    <div
      className={`bnk-docai-root${active ? ' bnk-docai-active' : ''}${className ? ` ${className}` : ''}`}
      data-scenario={scenarioId}
    >
      <style>{css}</style>

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <header className="bnk-docai-header">
        <span className="bnk-docai-icon-pill" aria-hidden="true">
          <FileSearch size={20} color={C.blue} strokeWidth={2.2} />
        </span>
        <div className="bnk-docai-header-text">
          <div className="bnk-docai-title-row">
            <h1 className="bnk-docai-h1">{tx('title')}</h1>
            <span className="bnk-docai-preview" title={tx('previewBadge')}>
              <span className="bnk-docai-preview-dot" aria-hidden="true" />
              {tx('previewBadge')}
            </span>
          </div>
          <p className="bnk-docai-subtitle">{tx('subtitle')}</p>
        </div>
        <span className="bnk-docai-aibadge">
          <Sparkles size={13} strokeWidth={2} aria-hidden="true" />
          {tx('aiBadge')}
        </span>
      </header>

      {/* ── Split workspace ────────────────────────────────────────────── */}
      <div className="bnk-docai-split">
        {/* LEFT — source document preview */}
        <section className="bnk-docai-source" aria-label={tx('docTitle')}>
          <div className="bnk-docai-source-bar">
            <FileText size={14} strokeWidth={2} color="#66a3ff" aria-hidden="true" />
            <span className="bnk-docai-source-meta">{tx('docMeta')}</span>
            <span className="bnk-docai-source-hint">
              <span className="bnk-docai-hint-swatch" aria-hidden="true" />
              {tx('highlightHint')}
            </span>
          </div>

          <div className="bnk-docai-paper-wrap">
            <article className="bnk-docai-paper">
              <h2 className="bnk-docai-paper-title">{tx('docTitle')}</h2>
              <p className="bnk-docai-paper-sub">{tx('docSubtitle')}</p>
              <div className="bnk-docai-paper-section">{tx('docSection')}</div>
              <div className="bnk-docai-paper-body">
                {docLines.map((line, i) => {
                  const isCyan = i === hl.cyan;
                  const isYellow = i === hl.yellow;
                  const cls = isCyan
                    ? ' bnk-docai-line--cyan'
                    : isYellow
                      ? ' bnk-docai-line--yellow'
                      : '';
                  return (
                    <p key={i} className={`bnk-docai-line${cls}`}>
                      {(isCyan || isYellow) && (
                        <span
                          className={`bnk-docai-line-tab bnk-docai-line-tab--${isCyan ? 'cyan' : 'yellow'}`}
                          aria-hidden="true"
                        />
                      )}
                      <span className="bnk-docai-line-text">{line}</span>
                    </p>
                  );
                })}
              </div>
              <div className="bnk-docai-paper-fade" aria-hidden="true" />
            </article>
          </div>

          <div className="bnk-docai-source-foot">
            <span className="bnk-docai-page-ind">{tx('pageIndicator')}</span>
          </div>
        </section>

        {/* RIGHT — analysis results */}
        <section className="bnk-docai-results" aria-label={tx('title')}>
          {isContract ? (
            <>
              <div className="bnk-docai-results-head">
                <ShieldAlert size={15} strokeWidth={2} color={C.amber} aria-hidden="true" />
                <span className="bnk-docai-results-title">{tx('title')}</span>
                <span className="bnk-docai-count-pill">{tx('findingsCount')}</span>
              </div>

              <ul className="bnk-docai-findings">
                {findings.map((f, i) => {
                  const s = SEVERITY_STYLE[f.severity];
                  return (
                    <li
                      key={i}
                      className="bnk-docai-finding"
                      style={{ animationDelay: `${0.12 + i * 0.06}s` }}
                    >
                      <span
                        className="bnk-docai-sev-rail"
                        style={{ background: s.color }}
                        aria-hidden="true"
                      />
                      <div className="bnk-docai-finding-main">
                        <div className="bnk-docai-finding-top">
                          <span
                            className="bnk-docai-sev-chip"
                            style={{ color: s.color, background: s.bg, borderColor: s.border }}
                          >
                            <CategoryIcon severity={f.severity} />
                            {t(k(s.labelKey))}
                          </span>
                          <span className="bnk-docai-finding-cat">{f.category}</span>
                        </div>
                        <h3 className="bnk-docai-finding-title">{f.title}</h3>
                        <p className="bnk-docai-finding-detail">{f.detail}</p>
                        <span className="bnk-docai-finding-source">{f.source}</span>
                      </div>
                    </li>
                  );
                })}
              </ul>

              <div className="bnk-docai-deadlines">
                <div className="bnk-docai-deadlines-head">
                  <CalendarClock size={14} strokeWidth={2} color={C.cyan} aria-hidden="true" />
                  {tx('deadlinesTitle')}
                </div>
                <ol className="bnk-docai-timeline">
                  {deadlines.map((d, i) => (
                    <li key={i} className="bnk-docai-tl-item">
                      <span className="bnk-docai-tl-node" aria-hidden="true" />
                      <div className="bnk-docai-tl-body">
                        <div className="bnk-docai-tl-row">
                          <span className="bnk-docai-tl-date">{d.date}</span>
                          <span className="bnk-docai-tl-label">{d.label}</span>
                        </div>
                        <span className="bnk-docai-tl-note">{d.note}</span>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>

              <p className="bnk-docai-footer">{tx('footerNote')}</p>
            </>
          ) : (
            <>
              <div className="bnk-docai-results-head">
                <ScanText size={15} strokeWidth={2} color={C.cyan} aria-hidden="true" />
                <span className="bnk-docai-results-title">{tx('kpiTitle')}</span>
                <span className="bnk-docai-count-pill">{tx('deltaQoQ')}</span>
              </div>

              <div className="bnk-docai-kpis">
                {kpis.map((kpi, i) => {
                  const tColor =
                    kpi.trend === 'up' ? C.green : kpi.trend === 'down' ? C.red : C.textSecondary;
                  return (
                    <div
                      key={i}
                      className="bnk-docai-kpi"
                      style={{ animationDelay: `${0.12 + i * 0.05}s` }}
                    >
                      <div className="bnk-docai-kpi-label">{kpi.label}</div>
                      <div className="bnk-docai-kpi-value">{kpi.value}</div>
                      <div className="bnk-docai-kpi-delta" style={{ color: tColor }}>
                        <TrendIcon trend={kpi.trend} />
                        {kpi.delta}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="bnk-docai-summary">
                <div className="bnk-docai-summary-head">
                  <Quote size={13} strokeWidth={2} color={C.blue} aria-hidden="true" />
                  {tx('summaryTitle')}
                </div>
                <p className="bnk-docai-summary-text">{tx('summary')}</p>
              </div>

              <div className="bnk-docai-table-card">
                <div className="bnk-docai-table-head">
                  <Table2 size={13} strokeWidth={2} color={C.purple} aria-hidden="true" />
                  {tx('tableTitle')}
                </div>
                <table className="bnk-docai-table">
                  <thead>
                    <tr>
                      {tableCols.map((col, i) => (
                        <th key={i} className={i === 0 ? 'bnk-docai-th-l' : 'bnk-docai-th-r'}>
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {tableRows.map((row, ri) => (
                      <tr key={ri}>
                        {row.map((cell, ci) => (
                          <td
                            key={ci}
                            className={
                              ci === 0
                                ? 'bnk-docai-td-l'
                                : ci === row.length - 1
                                  ? 'bnk-docai-td-delta'
                                  : 'bnk-docai-td-r'
                            }
                          >
                            {ci === 0 ? (
                              <span className="bnk-docai-td-seg">
                                <BarChart3 size={12} strokeWidth={2} color={C.textTertiary} aria-hidden="true" />
                                {cell}
                              </span>
                            ) : ci === row.length - 1 ? (
                              <span className="bnk-docai-td-delta-inner">
                                <ArrowUpRight size={11} strokeWidth={2.6} aria-hidden="true" />
                                {cell}
                              </span>
                            ) : (
                              cell
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <p className="bnk-docai-footer">
                <Percent size={11} strokeWidth={2} aria-hidden="true" style={{ opacity: 0.6 }} />
                {tx('footerNote')}
              </p>
            </>
          )}
        </section>
      </div>
    </div>
  );
}

const css = `
.bnk-docai-root {
  font-family: Inter, system-ui, sans-serif;
  color: ${C.textPrimary};
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  padding: 4px 2px 8px;
  box-sizing: border-box;
}
.bnk-docai-root *, .bnk-docai-root *::before, .bnk-docai-root *::after { box-sizing: border-box; }

/* Header */
.bnk-docai-header { display: flex; align-items: center; gap: 12px; margin-bottom: 16px; flex-shrink: 0; }
.bnk-docai-icon-pill { display: inline-flex; align-items: center; justify-content: center; width: 38px; height: 38px; border-radius: 11px; background: rgba(45,126,255,0.10); border: 1px solid rgba(45,126,255,0.22); flex-shrink: 0; }
.bnk-docai-header-text { flex: 1; min-width: 0; }
.bnk-docai-title-row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.bnk-docai-h1 { margin: 0; font-size: 21px; font-weight: 700; color: ${C.textPrimary}; letter-spacing: -0.01em; }
.bnk-docai-preview { display: inline-flex; align-items: center; gap: 6px; font-size: 11px; font-weight: 600; color: ${C.amber}; background: rgba(251,191,36,0.10); border: 1px solid rgba(251,191,36,0.28); border-radius: 999px; padding: 3px 9px 3px 8px; white-space: nowrap; }
.bnk-docai-preview-dot { width: 6px; height: 6px; border-radius: 50%; background: ${C.amber}; animation: bnk-docai-blink 2.2s ease-in-out infinite; }
.bnk-docai-subtitle { margin: 2px 0 0; font-size: 13px; color: ${C.textSecondary}; }
.bnk-docai-aibadge { display: inline-flex; align-items: center; gap: 6px; font-size: 12px; font-weight: 600; color: ${C.cyan}; background: linear-gradient(135deg, rgba(0,212,255,0.12), rgba(45,126,255,0.10)); border: 1px solid rgba(0,212,255,0.25); border-radius: 999px; padding: 5px 11px; flex-shrink: 0; }

/* Split workspace */
.bnk-docai-split { flex: 1; min-height: 0; display: grid; grid-template-columns: minmax(0, 46fr) minmax(0, 54fr); gap: 16px; }

/* LEFT — source document */
.bnk-docai-source { display: flex; flex-direction: column; min-height: 0; background: ${C.surface}; border: 1px solid ${C.border}; border-radius: 16px; overflow: hidden; box-shadow: 0 1px 2px rgba(0,0,0,0.3); }
.bnk-docai-source-bar { display: flex; align-items: center; gap: 8px; padding: 11px 14px; border-bottom: 1px solid ${C.border}; background: rgba(255,255,255,0.02); flex-shrink: 0; }
.bnk-docai-source-meta { font-size: 12px; color: ${C.textSecondary}; font-weight: 500; }
.bnk-docai-source-hint { display: inline-flex; align-items: center; gap: 6px; margin-left: auto; font-size: 11px; color: ${C.textTertiary}; }
.bnk-docai-hint-swatch { width: 18px; height: 8px; border-radius: 3px; background: linear-gradient(90deg, rgba(0,212,255,0.55), rgba(251,191,36,0.55)); }

.bnk-docai-paper-wrap { flex: 1; min-height: 0; overflow-y: auto; padding: 18px 16px; background: ${C.recessed}; scrollbar-width: none; }
.bnk-docai-paper-wrap::-webkit-scrollbar { width: 0; height: 0; display: none; }
.bnk-docai-paper { position: relative; background: #F4F1EA; color: #2A2620; border-radius: 6px; padding: 26px 28px 40px; box-shadow: 0 8px 24px rgba(0,0,0,0.45), 0 0 0 1px rgba(0,0,0,0.2); }
.bnk-docai-paper-title { margin: 0; font-size: 15.5px; font-weight: 700; color: #1C1A15; line-height: 1.3; letter-spacing: -0.01em; }
.bnk-docai-paper-sub { margin: 6px 0 0; font-size: 11.5px; color: #6B6457; font-style: italic; }
.bnk-docai-paper-section { margin: 18px 0 12px; font-size: 12px; font-weight: 700; color: #3A352C; padding-bottom: 7px; border-bottom: 1.5px solid #D8D2C4; letter-spacing: 0.01em; }
.bnk-docai-paper-body { display: flex; flex-direction: column; gap: 11px; }
.bnk-docai-line { position: relative; margin: 0; font-size: 12px; line-height: 1.65; color: #423C31; padding: 1px 0; }
.bnk-docai-line-text { position: relative; }
.bnk-docai-line--cyan .bnk-docai-line-text { background: linear-gradient(transparent 12%, rgba(0,196,232,0.32) 12% 92%, transparent 92%); box-shadow: 0 0 0 1px rgba(0,196,232,0.18); border-radius: 2px; padding: 0 1px; }
.bnk-docai-line--yellow .bnk-docai-line-text { background: linear-gradient(transparent 12%, rgba(255,214,64,0.5) 12% 92%, transparent 92%); box-shadow: 0 0 0 1px rgba(220,170,20,0.22); border-radius: 2px; padding: 0 1px; }
.bnk-docai-line-tab { position: absolute; left: -16px; top: 3px; bottom: 3px; width: 3px; border-radius: 2px; }
.bnk-docai-line-tab--cyan { background: #00C4E8; }
.bnk-docai-line-tab--yellow { background: #E8B400; }
.bnk-docai-paper-fade { position: absolute; left: 0; right: 0; bottom: 0; height: 36px; background: linear-gradient(transparent, #F4F1EA); border-radius: 0 0 6px 6px; }

.bnk-docai-source-foot { display: flex; align-items: center; justify-content: center; padding: 9px; border-top: 1px solid ${C.border}; background: rgba(255,255,255,0.02); flex-shrink: 0; }
.bnk-docai-page-ind { font-size: 11px; color: ${C.textSecondary}; background: ${C.elevated}; border: 1px solid ${C.border}; border-radius: 999px; padding: 3px 12px; font-weight: 500; }

/* RIGHT — results */
.bnk-docai-results { display: flex; flex-direction: column; min-height: 0; overflow-y: auto; padding-right: 2px; scrollbar-width: none; }
.bnk-docai-results::-webkit-scrollbar { width: 0; height: 0; display: none; }
.bnk-docai-results-head { display: flex; align-items: center; gap: 9px; margin-bottom: 12px; flex-shrink: 0; }
.bnk-docai-results-title { font-size: 15px; font-weight: 700; color: ${C.textPrimary}; }
.bnk-docai-count-pill { margin-left: auto; font-size: 11px; font-weight: 600; color: ${C.textSecondary}; background: ${C.elevated}; border: 1px solid ${C.border}; border-radius: 999px; padding: 3px 10px; }

/* Findings (contractCheck) */
.bnk-docai-findings { list-style: none; margin: 0 0 18px; padding: 0; display: flex; flex-direction: column; gap: 11px; }
.bnk-docai-finding { position: relative; display: flex; gap: 12px; background: ${C.surface}; border: 1px solid ${C.border}; border-radius: 13px; padding: 13px 15px 13px 13px; box-shadow: 0 1px 2px rgba(0,0,0,0.25); transition: border-color .15s, transform .15s, background .15s; }
.bnk-docai-finding:hover { border-color: ${C.borderStrong}; background: rgba(255,255,255,0.025); transform: translateX(2px); }
.bnk-docai-sev-rail { flex-shrink: 0; width: 3px; border-radius: 3px; align-self: stretch; }
.bnk-docai-finding-main { flex: 1; min-width: 0; }
.bnk-docai-finding-top { display: flex; align-items: center; gap: 9px; margin-bottom: 7px; flex-wrap: wrap; }
.bnk-docai-sev-chip { display: inline-flex; align-items: center; gap: 5px; font-size: 11px; font-weight: 700; text-transform: capitalize; border: 1px solid; border-radius: 999px; padding: 3px 9px 3px 7px; }
.bnk-docai-finding-cat { font-size: 12px; font-weight: 600; color: ${C.textSecondary}; }
.bnk-docai-finding-title { margin: 0 0 4px; font-size: 13.5px; font-weight: 600; color: ${C.textPrimary}; line-height: 1.35; }
.bnk-docai-finding-detail { margin: 0 0 8px; font-size: 12.5px; line-height: 1.5; color: ${C.textSecondary}; }
.bnk-docai-finding-source { display: inline-block; font-size: 11px; font-weight: 600; color: #66a3ff; background: rgba(45,126,255,0.10); border: 1px solid rgba(45,126,255,0.20); border-radius: 7px; padding: 3px 9px; }

/* Deadlines timeline */
.bnk-docai-deadlines { background: ${C.surface}; border: 1px solid ${C.border}; border-radius: 13px; padding: 15px 16px; margin-bottom: 14px; }
.bnk-docai-deadlines-head { display: flex; align-items: center; gap: 8px; font-size: 13px; font-weight: 700; color: ${C.textPrimary}; margin-bottom: 14px; }
.bnk-docai-timeline { list-style: none; margin: 0; padding: 0 0 0 4px; }
.bnk-docai-tl-item { position: relative; padding: 0 0 16px 22px; }
.bnk-docai-tl-item:last-child { padding-bottom: 0; }
.bnk-docai-tl-item::before { content: ''; position: absolute; left: 4px; top: 12px; bottom: -4px; width: 2px; background: ${C.border}; }
.bnk-docai-tl-item:last-child::before { display: none; }
.bnk-docai-tl-node { position: absolute; left: 0; top: 4px; width: 10px; height: 10px; border-radius: 50%; background: ${C.recessed}; border: 2px solid ${C.cyan}; box-shadow: 0 0 0 3px rgba(0,212,255,0.10); }
.bnk-docai-tl-row { display: flex; align-items: baseline; gap: 10px; flex-wrap: wrap; }
.bnk-docai-tl-date { font-size: 12.5px; font-weight: 700; color: ${C.cyan}; font-variant-numeric: tabular-nums; }
.bnk-docai-tl-label { font-size: 12.5px; font-weight: 600; color: ${C.textPrimary}; }
.bnk-docai-tl-note { display: block; font-size: 11.5px; color: ${C.textSecondary}; margin-top: 2px; }

/* KPI grid (docAnalysis) */
.bnk-docai-kpis { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-bottom: 14px; }
.bnk-docai-kpi { background: ${C.surface}; border: 1px solid ${C.border}; border-radius: 12px; padding: 13px 14px; box-shadow: 0 1px 2px rgba(0,0,0,0.25); transition: border-color .15s, transform .15s; }
.bnk-docai-kpi:hover { border-color: ${C.borderStrong}; transform: translateY(-2px); }
.bnk-docai-kpi-label { font-size: 11.5px; color: ${C.textSecondary}; margin-bottom: 6px; }
.bnk-docai-kpi-value { font-size: 19px; font-weight: 700; color: ${C.textPrimary}; letter-spacing: -0.01em; font-variant-numeric: tabular-nums; }
.bnk-docai-kpi-delta { display: inline-flex; align-items: center; gap: 3px; font-size: 11.5px; font-weight: 700; margin-top: 5px; font-variant-numeric: tabular-nums; }

/* Auto-summary */
.bnk-docai-summary { background: linear-gradient(135deg, rgba(45,126,255,0.07), rgba(0,212,255,0.04)); border: 1px solid rgba(45,126,255,0.22); border-radius: 13px; padding: 14px 16px; margin-bottom: 14px; }
.bnk-docai-summary-head { display: flex; align-items: center; gap: 8px; font-size: 12px; font-weight: 700; color: #66a3ff; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.04em; }
.bnk-docai-summary-text { margin: 0; font-size: 13px; line-height: 1.6; color: ${C.textPrimary}; }

/* Extracted table */
.bnk-docai-table-card { background: ${C.surface}; border: 1px solid ${C.border}; border-radius: 13px; padding: 14px 16px 6px; margin-bottom: 14px; }
.bnk-docai-table-head { display: flex; align-items: center; gap: 8px; font-size: 13px; font-weight: 700; color: ${C.textPrimary}; margin-bottom: 10px; }
.bnk-docai-table { width: 100%; border-collapse: collapse; font-size: 12.5px; }
.bnk-docai-table th { font-size: 11px; font-weight: 600; color: ${C.textTertiary}; text-transform: uppercase; letter-spacing: 0.03em; padding: 0 0 8px; border-bottom: 1px solid ${C.border}; }
.bnk-docai-th-l { text-align: left; }
.bnk-docai-th-r { text-align: right; }
.bnk-docai-table td { padding: 9px 0; border-bottom: 1px solid rgba(30,45,69,0.5); }
.bnk-docai-table tbody tr:last-child td { border-bottom: none; }
.bnk-docai-td-l { color: ${C.textPrimary}; font-weight: 500; }
.bnk-docai-td-seg { display: inline-flex; align-items: center; gap: 7px; }
.bnk-docai-td-r { text-align: right; color: ${C.textSecondary}; font-variant-numeric: tabular-nums; }
.bnk-docai-td-delta { text-align: right; }
.bnk-docai-td-delta-inner { display: inline-flex; align-items: center; gap: 3px; color: ${C.green}; font-weight: 700; font-variant-numeric: tabular-nums; }

/* Footer note */
.bnk-docai-footer { display: flex; align-items: center; gap: 6px; margin: 2px 0 0; font-size: 11px; color: ${C.textTertiary}; font-style: italic; }

/* Entrance animations — gated on .bnk-docai-active; finished look when inactive */
@keyframes bnk-docai-blink { 0%, 100% { opacity: 1; } 50% { opacity: 0.35; } }
@keyframes bnk-docai-rise { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
@keyframes bnk-docai-fade { from { opacity: 0; } to { opacity: 1; } }
.bnk-docai-active .bnk-docai-source { animation: bnk-docai-rise .5s cubic-bezier(.22,.61,.36,1) both; }
.bnk-docai-active .bnk-docai-results-head { animation: bnk-docai-fade .5s ease both .08s; }
.bnk-docai-active .bnk-docai-finding,
.bnk-docai-active .bnk-docai-kpi { animation: bnk-docai-rise .5s cubic-bezier(.22,.61,.36,1) both; }
.bnk-docai-active .bnk-docai-deadlines,
.bnk-docai-active .bnk-docai-summary { animation: bnk-docai-rise .5s cubic-bezier(.22,.61,.36,1) both .42s; }
.bnk-docai-active .bnk-docai-table-card { animation: bnk-docai-rise .5s cubic-bezier(.22,.61,.36,1) both .5s; }

@media (max-width: 720px) {
  .bnk-docai-split { grid-template-columns: 1fr; }
  .bnk-docai-kpis { grid-template-columns: repeat(2, 1fr); }
}
@media (prefers-reduced-motion: reduce) {
  .bnk-docai-active .bnk-docai-source,
  .bnk-docai-active .bnk-docai-results-head,
  .bnk-docai-active .bnk-docai-finding,
  .bnk-docai-active .bnk-docai-kpi,
  .bnk-docai-active .bnk-docai-deadlines,
  .bnk-docai-active .bnk-docai-summary,
  .bnk-docai-active .bnk-docai-table-card { animation: none; }
  .bnk-docai-preview-dot { animation: none; }
}
`;

export default DocAnalysisScreen;
